/**
 * Passage highlight utility for the citation trust Source Panel.
 *
 * Finds the longest common substring between a citation excerpt and the
 * document chunk that (probably) contains it, so the passage can be
 * visually highlighted at the text level (no PDF, no LLM call).
 */

interface NormalizedForm {
  /** Lowercased text with runs of whitespace collapsed to a single space. */
  normalized: string;
  /** normalized[i] was produced from original text at indexMap[i]. */
  indexMap: number[];
}

/**
 * Builds a whitespace-collapsed, lowercased view of `text`, while keeping
 * a per-character map back to the original string so a match found in the
 * normalized form can be sliced out of the ORIGINAL text (preserving its
 * real formatting) instead of the collapsed one.
 */
function buildNormalizedForm(text: string): NormalizedForm {
  let normalized = '';
  const indexMap: number[] = [];
  let inWhitespaceRun = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (/\s/.test(ch)) {
      if (!inWhitespaceRun) {
        normalized += ' ';
        indexMap.push(i);
        inWhitespaceRun = true;
      }
    } else {
      normalized += ch.toLowerCase();
      indexMap.push(i);
      inWhitespaceRun = false;
    }
  }

  return { normalized, indexMap };
}

export interface HighlightSpan {
  /** Start index (inclusive) into the ORIGINAL `content` string. */
  start: number;
  /** End index (exclusive) into the ORIGINAL `content` string. */
  end: number;
}

/**
 * Finds the longest common substring (>= minLength, measured on the
 * whitespace-collapsed/lowercased form) between `excerpt` and `content`,
 * and returns its span within the original `content` string.
 *
 * Returns null if no match of at least `minLength` is found.
 */
export function findHighlightSpan(
  excerpt: string | null | undefined,
  content: string,
  minLength = 40
): HighlightSpan | null {
  if (!excerpt || !content) return null;

  const a = buildNormalizedForm(excerpt);
  const b = buildNormalizedForm(content);

  const n = a.normalized.length;
  const m = b.normalized.length;
  if (n === 0 || m === 0) return null;

  // Classic longest-common-substring DP, rolling two rows to keep memory low.
  let prevRow = new Int32Array(m + 1);
  let currRow = new Int32Array(m + 1);

  let bestLength = 0;
  let bestEndB = 0; // exclusive end index in the normalized `content`

  for (let i = 1; i <= n; i++) {
    const aChar = a.normalized[i - 1];
    for (let j = 1; j <= m; j++) {
      if (aChar === b.normalized[j - 1]) {
        const val = prevRow[j - 1] + 1;
        currRow[j] = val;
        if (val > bestLength) {
          bestLength = val;
          bestEndB = j;
        }
      } else {
        currRow[j] = 0;
      }
    }
    const swap = prevRow;
    prevRow = currRow;
    currRow = swap;
    currRow.fill(0);
  }

  if (bestLength < minLength) return null;

  const startNormB = bestEndB - bestLength;
  const endNormBInclusive = bestEndB - 1;

  const origStart = b.indexMap[startNormB];
  const origEndInclusive = b.indexMap[endNormBInclusive];

  return { start: origStart, end: origEndInclusive + 1 };
}
